<template>
  <BlBackdrop
    label="Done"
    :disabled="isDisabledSave"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSave"
  >
    <div class="px-8 pt-8">
      <div class="mb-1">
        <BlTextBody2 v-if="title === 'create'" text="Create new availability" />
      </div>
      <div class="flex flex-col gap-4" :style="{ width: '40rem' }">
        <BlTextTitle4 text="Custom repeat" />
        <div class="grid grid-cols-4 gap-x-2 gap-y-4">
          <BlTextSubtitle3 text="Repeat every" class="my-1" />
          <div class="col-span-3 flex items-center gap-2">
            <BlInputText
              class="w-16"
              :input="intervalInput"
              @changed="inputHandler"
              dense
            />
            <BlSelectBox
              class="max-w-24"
              v-model="repetitionTypeInput"
              placeholder="Select room"
              :options="repetitionTypeOptions"
              @change="repetitionTypeInput = $event"
              dense
            />
          </div>
          <BlTextSubtitle3
            v-if="
              repetitionTypeInput === 'week' || repetitionTypeInput === 'month'
            "
            class="my-1"
            text="On"
          />
          <div
            v-if="repetitionTypeInput === 'week'"
            class="col-span-3 flex items-center gap-2"
          >
            <button
              v-for="day in daysOfWeekOptions"
              :key="day.value"
              :title="day.value"
              class="outline-none h-8 w-8 flex items-center justify-center rounded-full border"
              :class="
                daysOfWeekInput.findIndex(i => i === day.value) > -1
                  ? 'border-light-purple bg-light-purple'
                  : 'border-slate-500 bg-pure-white'
              "
              @click="onChangeDays(day.value)"
            >
              <BlTextBody2
                :text="day.label"
                :class="
                  daysOfWeekInput.findIndex(i => i === day.value) > -1
                    ? 'text-pure-white'
                    : 'text-slate-1000'
                "
              />
            </button>
          </div>
          <div
            v-if="repetitionTypeInput === 'month'"
            class="col-span-3 flex items-center"
          >
            <button
              v-for="(opt, idx) in onMonthOptions"
              :key="opt.input"
              :title="opt.value"
              class="outline-none h-8 px-3 flex items-center justify-center border border-slate-500"
              :class="[
                (opt.input === 'day' && dayOfMonthInput) ||
                (opt.input === 'week' && weekOfMonthInput)
                  ? 'bg-slate-300'
                  : 'bg-pure-white',
                idx === 0 ? 'rounded-l' : 'rounded-r'
              ]"
              @click="onChangeOnMonth(opt)"
            >
              <BlTextBody2
                :text="opt.label"
                :class="[
                  (opt.input === 'day' && dayOfMonthInput) ||
                  (opt.input === 'week' && weekOfMonthInput)
                    ? 'text-slate-1000'
                    : 'text-slate-700'
                ]"
              />
            </button>
          </div>
          <BlTextSubtitle3 text="Ends" />
          <div class="col-span-3 flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <BlInputRadio
                name="repeat_end"
                :checked="repeatEndInput === 'never'"
                @changed="repeatEndInput = 'never'"
              />
              <BlTextBody2 text="Never" />
            </div>
            <div class="flex items-center gap-2">
              <BlInputRadio
                name="repeat_end"
                :checked="repeatEndInput === 'on_date'"
                @changed="repeatEndInput = 'on_date'"
              />
              <BlTextBody2 text="On" />
              <BlInputDate
                class="w-36"
                :input="endAfterInput"
                @changed="inputHandler"
                dense
              />
            </div>
            <div class="flex items-center gap-2">
              <BlInputRadio
                name="repeat_end"
                :checked="repeatEndInput === 'after_occurrences'"
                @changed="repeatEndInput = 'after_occurrences'"
              />
              <BlTextBody2 text="After" />
              <BlInputText
                class="w-16"
                :input="occurrencesInput"
                @changed="inputHandler"
                dense
              />
              <BlTextBody2 text="time(s)" />
            </div>
          </div>
        </div>
        <div
          v-if="repeatEndInput === 'never'"
          class="px-4 py-2 bg-cornflower-200 rounded flex items-start gap-2"
        >
          <BlIcon icon="info" color="cornflower-8" class="h-6 w-6" />
          <div>
            <BlTextBody2
              class="text-cornflower-800"
              text="If you select 'never ends,' the availability will be generated for up to 6 months, with new slots generated weekly every Sunday at 00:00 WIB (17:00 UTC). Feel free to share any feedback with us at "
            />
            <a href="mailto:blum@simpul.tech" target="_blank">
              <BlTextBody2
                class="text-cornflower-800 underline"
                text="blum@simpul.tech."
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import moment from "moment";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "AvailabilityRepeatDetailsModal",
  components: {
    BlBackdrop,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputText,
    BlInputDate,
    BlInputRadio,
    BlSelectBox,
    BlIcon
  },
  props: {
    title: { type: String, default: "" },
    current: { type: Object, default: null }
  },
  data() {
    return {
      intervalInput: { model: "1", inputType: "number", min: 1 },
      repetitionTypeInput: "week",
      repetitionTypeOptions: [
        // { value: "day", label: "Day" },
        { value: "week", label: "Week" },
        { value: "month", label: "Month" },
        { value: "year", label: "Year" }
      ],
      repeatEndInput: "never", // never | on_date | after_occurrences
      daysOfWeekInput: [], // (monday | tuesday | wednesday | thursday | friday | saturday | sunday)[]
      daysOfWeekOptions: [
        { value: "monday", label: "Mo" },
        { value: "tuesday", label: "Tu" },
        { value: "wednesday", label: "We" },
        { value: "thursday", label: "Th" },
        { value: "friday", label: "Fr" },
        { value: "saturday", label: "Sa" },
        { value: "sunday", label: "Su" }
      ],
      dayOfMonthInput: null, // 1 - 31 (day)
      weekOfMonthInput: null, // first, second, third, fourth, last (week)
      endAfterInput: { model: "", disabled: true },
      occurrencesInput: {
        model: "1",
        inputType: "number",
        min: 1,
        disabled: true
      }
    };
  },
  computed: {
    onMonthOptions() {
      const date = this.current.startDateInput;
      const numberOfDay = moment(date).format("D");
      const numberOfWeek = Math.ceil(numberOfDay / 7);
      const numbers = ["", "first", "second", "third", "fourth", "last"];
      const ordinal = numbers[numberOfWeek];
      const ordinals = {
        first: "1st",
        second: "2nd",
        third: "3rd",
        fourth: "4th",
        last: "5th"
      };
      const dayOfWeek = moment(date).format("ddd");
      const arr = [
        {
          input: "day",
          value: numberOfDay,
          label: `Monthly on day ${numberOfDay}`
        },
        {
          input: "week",
          value: ordinal,
          label: `Monthly on the ${ordinals[ordinal]} ${dayOfWeek}`
        }
      ];
      return arr;
    },
    isDisabledSave() {
      if (!this.intervalInput.model || this.intervalInput.model === "0") {
        return true;
      }

      if (this.repetitionTypeInput === "week") {
        if (!this.daysOfWeekInput.length) return true;
      }
      if (this.repetitionTypeInput === "month") {
        if (!this.dayOfMonthInput && !this.weekOfMonthInput) return true;
      }
      if (this.repeatEndInput === "on_date") {
        if (!this.endAfterInput.model) return true;
      }
      if (this.repeatEndInput === "after_occurrences") {
        if (
          !this.occurrencesInput.model ||
          this.occurrencesInput.model === "0"
        ) {
          return true;
        }
      }
      return false;
    }
  },
  watch: {
    repeatEndInput(val) {
      if (val === "never") {
        this.endAfterInput.disabled = true;
        this.occurrencesInput.disabled = true;
      }
      if (val === "on_date") {
        this.endAfterInput.disabled = false;
        this.occurrencesInput.disabled = true;
      }
      if (val === "after_occurrences") {
        this.endAfterInput.disabled = true;
        this.occurrencesInput.disabled = false;
      }
    }
  },
  created() {
    const date = this.current.startDateInput;
    const dayOfWeek = this.lowercaseFirstLetter(moment(date).format("dddd"));

    this.intervalInput.model = this.current.intervalInput;
    this.repetitionTypeInput = this.current.repetitionTypeInput;
    this.repeatEndInput = this.current.repeatEndInput;
    if (!this.current.daysOfWeekInput.length) {
      this.daysOfWeekInput = [dayOfWeek];
    } else {
      this.daysOfWeekInput = this.current.daysOfWeekInput;
    }
    this.dayOfMonthInput = this.current.dayOfMonthInput;
    this.weekOfMonthInput = this.current.weekOfMonthInput;
    this.endAfterInput.model = this.current.endAfterInput;
    this.occurrencesInput.model = this.current.occurrencesInput;
  },
  methods: {
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    lowercaseFirstLetter(string) {
      return string.charAt(0).toLowerCase() + string.slice(1);
    },
    onChangeDays(day) {
      const found = this.daysOfWeekInput.find(i => i === day);
      if (found) {
        this.daysOfWeekInput = this.daysOfWeekInput.filter(i => i !== day);
      } else this.daysOfWeekInput.push(day);
    },
    onChangeOnMonth(opt) {
      if (opt.input === "day") {
        this.dayOfMonthInput = opt.value;
        this.weekOfMonthInput = null;
      }
      if (opt.input === "week") {
        this.dayOfMonthInput = null;
        this.weekOfMonthInput = opt.value;
      }
    },
    onSave() {
      const days = this.daysOfWeekOptions.map(i => i.value);
      const data = {
        intervalInput: this.intervalInput.model,
        repetitionTypeInput: this.repetitionTypeInput,
        repeatEndInput: this.repeatEndInput,
        //
        daysOfWeekInput: this.daysOfWeekInput.sort(
          (a, b) => days.indexOf(a) - days.indexOf(b)
        ),
        dayOfMonthInput: this.dayOfMonthInput,
        weekOfMonthInput: this.weekOfMonthInput,
        //
        endAfterInput: this.endAfterInput.model,
        occurrencesInput: this.occurrencesInput.model
      };
      this.$emit("on-submit", data);
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/timeslot/RepeatDetailsModal.vue